import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Stethoscope, Printer, FileDown } from "lucide-react";
import { NarrativeBlock, ClinicianNote } from "./ReportView";
import AudioPlayer from "@/components/AudioPlayer";
import LanguagePicker from "@/components/LanguagePicker";
import { t } from "@/lib/i18n";
import { format } from "date-fns";
import { toast } from "sonner";

const RTL_LANGS = new Set(["ar", "he", "fa", "ur"]);

export default function SharedReport() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);
  const [lang, setLang] = useState("en");
  const [currentNarrative, setCurrentNarrative] = useState("");
  const [currentNote, setCurrentNote] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/shared/${token}`);
        setReport(data);
        setCurrentNarrative(data.translations?.en || data.patient_narrative || "");
        const sourceLang = data.clinician_note_lang || "en";
        const noteEn = (data.clinician_note_translations || {}).en
          || (sourceLang === "en" ? data.clinician_note : "")
          || "";
        setCurrentNote(noteEn);
      } catch {
        setError(true);
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="font-display text-2xl">Link expired</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--color-text-secondary)" }}>This shared report is no longer available.</p>
        </div>
      </div>
    );
  }
  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: "var(--bg-canvas)" }}>
      <header className="max-w-3xl mx-auto flex items-center justify-between mb-8 no-print">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary)" }}>
            <Stethoscope className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-semibold">MedNarrative</span>
        </Link>
        <div className="flex items-center gap-2">
          <button data-testid="shared-print-button" onClick={() => window.print()} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-white" style={{ borderColor: "var(--color-border)" }} title={t(lang, "print")}>
            <Printer className="w-4 h-4" />
          </button>
          <button
            data-testid="shared-download-pdf-button"
            onClick={async () => {
              try {
                const res = await api.get(`/shared/${token}/pdf`, { params: { mode: "patient", lang }, responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const dispo = res.headers?.["content-disposition"] || "";
                const m = /filename="?([^"]+)"?/i.exec(dispo);
                const a = document.createElement("a");
                a.href = url;
                a.download = m?.[1] || `${report.patient_name}_patient.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch {
                toast.error("Couldn't generate PDF");
              }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium"
            style={{ background: "var(--color-primary)" }}
          >
            <FileDown className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </header>

      <article
        className="print-area max-w-3xl mx-auto bg-white p-8 md:p-16 border rounded-lg shadow-sm"
        style={{ borderColor: "var(--color-border)" }}
        dir={RTL_LANGS.has(lang) ? "rtl" : "ltr"}
      >
        <div className="text-xs uppercase tracking-widest font-display" style={{ color: "var(--color-text-secondary)" }}>
          {report.modality} · {format(new Date(report.created_at), "d MMM yyyy")}
        </div>
        <h2 className="font-display text-3xl mt-3 mb-6" style={{ color: "var(--color-text-primary)" }}>
          {t(lang, "sharedGreeting", { name: report.patient_name })}
        </h2>
        <LanguagePicker
          value={lang}
          onChange={(code, { narrative, clinician_note }) => {
            setLang(code);
            setCurrentNarrative(narrative);
            setCurrentNote(clinician_note ?? "");
          }}
          source={{ type: "shared", id: token }}
          initialTranslations={report.translations || { en: report.patient_narrative }}
          initialNoteTranslations={report.clinician_note_translations || {}}
        />
        <AudioPlayer source={{ type: "shared", id: token }} language={lang} />
        <ClinicianNote note={currentNote || report.clinician_note} language={lang} sourceLang={report.clinician_note_lang || "en"} readOnly />
        <NarrativeBlock text={currentNarrative} serif />
        <div className="mt-12 pt-6 border-t text-xs" style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}>
          {t(lang, "sharedDisclaimer")}
        </div>
      </article>
    </div>
  );
}
